'use client';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import PortableText from '@/components/PortableText';
import Image from '@/components/Image';
import ParallaxImage from '@/components/animations/ParallaxImage';
import type { HeroCTASectionProps } from './types';
import CTA from '@/blocks/components/CTA';
import Background from '@/blocks/components/Background';

const HeroCTASection = ({
  title,
  description,
  mediaType,
  image,
  video,
  primaryCta,
  secondaryCta,
  backgroundColor = 'white',
  nextItemIsProductExampleImages,
}: HeroCTASectionProps) => (
  <Background backgroundColor={backgroundColor}>
    <Container
      maxWidth='lg'
      sx={{
        maxWidth: {
          lg: '1280px', // Explicitly set for laptop screens
        },
        '@media (min-width: 2000px)': {
          maxWidth: '1600px', // Only for very large screens
        },
        mx: 'auto',
        position: 'relative',
        pb: nextItemIsProductExampleImages ? 0 : 2,
      }}
    >
      {mediaType === 'image' && image ? (
        <ParallaxImage intensity={24}>
          <Image
            src={image}
            alt={title || 'Product header image'}
            aspectRatio='16/8'
            sx={{
              '& img': {
                objectFit: 'cover',
                objectPosition: 'center',
                borderRadius: nextItemIsProductExampleImages ? '12px 12px 0 0' : 3,
              },
            }}
            sizes='(max-width: 1280px) 100vw, 1280px'
          />
        </ParallaxImage>
      ) : mediaType === 'video' && video ? (
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            aspectRatio: '16/8',
            borderRadius: 3,
            overflow: 'hidden',
          }}
        >
          <video
            src={video}
            autoPlay
            loop
            muted
            playsInline
            style={{
              width: '100%',
              height: '113%',
              objectFit: 'cover',
              objectPosition: 'center',
            }}
            aria-label={title}
          />
        </Box>
      ) : null}

      {/* No scroll reveal: the hero card is above the fold and must paint with the SSR HTML */}
      {title && description && (
        <Stack
          direction='column'
          spacing={0.5}
          sx={{
            alignItems: 'center',
            textAlign: 'center',
            position: 'relative',
            mt: { xs: -3, md: -6 },
            bgcolor: 'background.default',
            zIndex: 1,
            maxWidth: { xs: '100%', md: 'calc(100% - 16.666%)' },
            mx: 'auto',
            pt: '24px',
            pb: '24px',
            px: '36px',
            borderRadius: 3,
            boxShadow: 0,
          }}
        >
          <Typography
            component='h3'
            sx={{
              color: 'primary.main',
              fontWeight: 700,
              lineHeight: 1.2,
              textAlign: 'center',
              fontSize: { xs: '40px', md: '56px' },
            }}
          >
            {title}
          </Typography>

          <Box
            sx={{
              '& p': {
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.7,
                color: 'text.primary',
                margin: 0,
                textAlign: 'center',
              },
            }}
          >
            <PortableText blocks={description} />
          </Box>

          {primaryCta?.visible && (
            <CTA
              href={primaryCta?.href || ''}
              text={primaryCta?.text || ''}
              color='secondary'
              variant='contained'
              hidden={primaryCta?.hidden}
              newTab={primaryCta?.newTab}
            />
          )}

          {secondaryCta?.visible && (
            <CTA
              variant='outlined'
              color='primary'
              href={secondaryCta?.href || ''}
              text={secondaryCta?.text || ''}
              hidden={primaryCta?.hidden}
              newTab={primaryCta?.newTab}
            />
          )}
        </Stack>
      )}
    </Container>
  </Background>
);

export { HeroCTASection, type HeroCTASectionProps };
